import { useEffect, useState } from "react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import AvatarUploader from "@/components/AvatarUploader";
import { SignedImage } from "@/lib/signedFile";
import { toast } from "sonner";
import { Loader2, Save, Building2 } from "lucide-react";

/**
 * Association profile management — lets the association update its public-facing
 * organization details. Uses the existing PATCH /auth/profile endpoint (already
 * supports name, phone, city, bio, avatar_url, organization_name for any role).
 */
export default function AssociationProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", organization_name: "", phone: "", city: "", bio: "", avatar_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        organization_name: user.organization_name || "",
        phone: user.phone || "",
        city: user.city || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || "",
      });
      setLoading(false);
    }
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.organization_name.trim()) {
      setErr("الاسم الرسمي واسم الجمعية مطلوبان");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      toast.success("تم حفظ الملف التعريفي");
      if (refresh) await refresh();
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="association-profile">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إعدادات الجمعية</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">إدارة الملف التعريفي</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            هذه البيانات تُعرض للطلاب والموجّهين ومدير المنصة ضمن الفرص المرتبطة بجمعيتك.
          </p>
        </div>

        <form onSubmit={save} noValidate className="grid gap-6 lg:grid-cols-3">
          {/* Preview card */}
          <aside className="lg:col-span-1" data-testid="assoc-profile-preview">
            <div className="rounded-2xl bg-white border border-cream-200 p-4 card-shadow">
              <div className="flex flex-col items-center text-center">
                <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white text-2xl font-heading font-black overflow-hidden">
                  {form.avatar_url ? (
                    <SignedImage fileId={form.avatar_url} alt="" className="h-full w-full object-cover" fallback={<Building2 className="h-10 w-10" strokeWidth={1.5} />} />
                  ) : (
                    <Building2 className="h-10 w-10" strokeWidth={1.5} />
                  )}
                </div>
                <div className="mt-4 font-heading font-bold text-slate-900 text-lg">{form.organization_name || form.name || "اسم الجمعية"}</div>
                <div className="mt-1 inline-flex px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 text-xs font-bold">
                  {ROLE_LABELS[user.role]}
                </div>
                <div className="mt-2 text-xs text-slate-500" dir="ltr">{user.email}</div>
                {form.city && <div className="mt-2 text-xs text-slate-600">{form.city}</div>}
                {form.bio && <p className="mt-4 text-sm text-slate-700 leading-relaxed">{form.bio}</p>}
              </div>
            </div>
          </aside>

          {/* Fields */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-white border border-cream-200 p-4 lg:p-8 card-shadow space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="اسم ممثل الجمعية" value={form.name} onChange={(v) => update("name", v)} testid="ap-name" required />
                <Field label="اسم الجمعية الرسمي" value={form.organization_name} onChange={(v) => update("organization_name", v)} testid="ap-org" required />
                <Field label="رقم الجوال" dir="ltr" value={form.phone} onChange={(v) => update("phone", v)} testid="ap-phone" />
                <Field label="المدينة" value={form.city} onChange={(v) => update("city", v)} testid="ap-city" placeholder="مثال: الرياض" />
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">شعار الجمعية (اختياري)</label>
                  <AvatarUploader
                    value={form.avatar_url}
                    onChange={(url) => update("avatar_url", url)}
                    size={96}
                    shape="square"
                    fallbackIcon={Building2}
                    testid="ap-avatar"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">نبذة عن الجمعية</label>
                  <textarea
                    rows={4}
                    value={form.bio}
                    onChange={(e) => update("bio", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    placeholder="اذكر رسالة الجمعية، مجالاتها الرئيسية، أثرها في القطاع غير الربحي..."
                    data-testid="ap-bio"
                  />
                  <div className="mt-1 text-xs text-slate-500">تُعرض هذه النبذة للطلاب أثناء استعراضهم للفرص المرتبطة بجمعيتك.</div>
                </div>
              </div>

              {err && (
                <div role="alert" className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100" data-testid="ap-error">{err}</div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60 transition-colors"
                  data-testid="ap-save"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  حفظ التعديلات
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, testid, type = "text", dir, required, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {label} {required && <span className="text-red-500" aria-hidden="true">*</span>}
      </label>
      <input
        type={type}
        dir={dir}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
        data-testid={testid}
        required={required}
        placeholder={placeholder}
      />
    </div>
  );
}
